import { useCallback } from "react";
import type { Trip } from "@/api/types";
import { useToast } from "@/components/toast/ToastProvider";

/** Outcome of a copy attempt, so callers can react instead of guessing. */
export type CopyResult = "copied" | "unavailable" | "failed";

/**
 * Copy-the-trip-link share behaviour (US-04). One implementation shared by
 * the trip header's ⤴ action and the post-create <SaveLinkStep> interstitial,
 * instead of duplicating the clipboard + toast logic.
 *
 * The shared link is `<origin>/t/<trip_id>` — with the no-accounts model
 * (ADR-0002) the slug *is* the credential, so this is the only way to
 * "save" a trip. On success the «Лінку скопійовано» toast confirms the copy.
 *
 * Returns a {@link CopyResult} so a caller can degrade gracefully (AC-04.8):
 * the previous `navigator.clipboard?.writeText` optional-chain silently
 * no-op'd when the API was absent — no toast, no error. Now an absent or
 * rejecting clipboard resolves to `"unavailable"` / `"failed"` and the
 * caller (e.g. <SaveLinkStep>) can show its manual-copy hint. The header's
 * onShare ignores the return value, which is fine — the toast still fires.
 */
export function useShareTrip(
  trip: Pick<Trip, "trip_id"> | undefined,
): () => Promise<CopyResult> {
  const toast = useToast();
  const tripId = trip?.trip_id;

  return useCallback(async (): Promise<CopyResult> => {
    if (!tripId) return "failed";
    const url = `${window.location.origin}/t/${tripId}`;
    if (!navigator.clipboard?.writeText) return "unavailable";
    try {
      await navigator.clipboard.writeText(url);
      toast.push({ tone: "success", title: "Лінку скопійовано" });
      return "copied";
    } catch {
      return "failed";
    }
  }, [tripId, toast]);
}
